import React from 'react';
import { Text, Center, Stack, Box } from '@chakra-ui/react';
import { Card, CardTitleSection } from 'components/Card';
import { ChartLoadingOverlay } from 'components/Charts';
import { AnimatePresence } from 'framer-motion';
import { DIMENSIONS } from './consts';
import { PredictionBarChart } from './PredictionBarChart';
import { useGrossIncomePrediction } from 'hooks/data';
import { TStoreId } from 'domain/stores';
import { TSectionCode } from 'domain/core';
import { useTranslation } from 'react-i18next';
import * as O from 'fp-ts/Option';
import { pipe } from 'fp-ts/lib/function';
import { GrossIncomePrediction } from 'domain/predictions';
import { RectData } from './types';

interface Props {
  date: string;
  storeId: TStoreId;
  sectionId: TSectionCode;
}

export const DailyTaskIncomePrediction: React.FC<Props> = ({ storeId, sectionId, date }) => {
  const { t } = useTranslation('whisperme');
  const { data, isFetching } = useGrossIncomePrediction({
    date: date,
    store_id: storeId,
    section_id: sectionId,
  });

  const getIncomeLegendLabel = (labelText: string) => {
    const labelMap: Record<string, string> = {
      history: t('DAILY_TASKS.INCOME_PREDICTION.INCOME_LEGEND.GROSS_INCOME'),
      quantile_5: t('DAILY_TASKS.INCOME_PREDICTION.INCOME_LEGEND.MIN_PREDICTION'),
      quantile_95: t('DAILY_TASKS.INCOME_PREDICTION.INCOME_LEGEND.EXPECTED_GROSS_INCOME'),
      budget: t('DAILY_TASKS.INCOME_PREDICTION.INCOME_LEGEND.BUDGET_THRESHOLD'),
    };

    return labelMap[labelText];
  };

  const formatDataForIncomeChart = (data: GrossIncomePrediction | undefined): RectData[] => {
    if (
      !data ||
      !data.historic_gross_income ||
      !data.prediction_gross_income ||
      !data.historic_prediction_gross_income ||
      !data.store_budgets
    ) {
      return [];
    }

    const historicEntries = Object.entries(data.historic_gross_income).map(([key, value]) => ({
      date: key,
      history: typeof value === 'number' ? value : 0,
      quantile_5: 0,
      quantile_95: 0,
      budget: 0,
    }));

    const historicPredictionEntries = Object.entries(data.historic_prediction_gross_income).map(([key, value]) => {
      const historicPredictionCount = pipe(
        value,
        O.map((value) => value),
        O.toNullable,
      );

      return {
        date: key,
        history: 0,
        quantile_5: historicPredictionCount?.quantile_5 ?? 0,
        quantile_95: (historicPredictionCount?.quantile_95 || 0) - (historicPredictionCount?.quantile_5 || 0),
        budget: 0,
      };
    });

    const predictionCount = data.prediction_gross_income;

    const predictionEntry = {
      date: predictionCount.date || '',
      history: -1,
      quantile_5: predictionCount.quantile_5 || 0,
      quantile_95: (predictionCount.quantile_95 || 0) - (predictionCount.quantile_5 || 0),
      budget: 0,
    };

    return [...historicPredictionEntries, ...historicEntries, predictionEntry];
  };

  return (
    <Card>
      <CardTitleSection as="h2">
        {t('DAILY_TASKS.INCOME_PREDICTION.INCOME_PREDICTION_LABEL', { date: date })}
      </CardTitleSection>
      <Stack
        flexDirection="column"
        flex="1 1 auto"
        minWidth={DIMENSIONS.minWidth}
        minHeight={DIMENSIONS.minHeight}
        position="relative"
      >
        {!!data && O.isSome(data.prediction_gross_income) && data.historic_gross_income ? (
          <Box w="100%" h="400px">
            <PredictionBarChart
              labelGetter={getIncomeLegendLabel}
              isCurrencySymbolUsed={true}
              parseDataForChart={formatDataForIncomeChart}
              data={pipe(
                data,
                ({
                  historic_gross_income,
                  prediction_gross_income,
                  historic_prediction_gross_income,
                  store_budgets,
                }) => ({
                  historic_gross_income,
                  historic_prediction_gross_income,
                  prediction_gross_income: pipe(
                    prediction_gross_income,
                    O.map((prediction) => prediction),
                    O.getOrElse(() => {
                      return {
                        date: date,
                        quantile_5: 0,
                        quantile_25: 0,
                        quantile_50: 0,
                        quantile_75: 0,
                        quantile_95: 0,
                      };
                    }),
                  ),
                  store_budgets,
                }),
              )}
            />
          </Box>
        ) : (
          <Center>
            <Text as="span">{t('COMPONENTS.NO_DATA_LABEL')}</Text>
          </Center>
        )}
        <AnimatePresence>{isFetching && <ChartLoadingOverlay />}</AnimatePresence>
      </Stack>
    </Card>
  );
};
